import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
  type CountryCallingCode,
} from 'libphonenumber-js';

const VALID_DIAL_CODES = new Set<string>(
  getCountries().map((c: CountryCode) =>
    getCountryCallingCode(c as CountryCode),
  ),
);

/**
 * Format a phone number as the user types, for the given country.
 * Returns the formatted national-format string (what the user sees in the input).
 */
export function formatAsYouType(input: string, country: string): string {
  const formatter = new AsYouType(country.toUpperCase() as CountryCode);
  return formatter.input(input);
}

/**
 * Parse raw input to an E.164 phone number for the given country.
 * Returns `+<dialcode><national>` if parseable; empty string otherwise.
 */
export function toE164(input: string, country: string): string {
  const parsed = parsePhoneNumberFromString(
    input,
    country.toUpperCase() as CountryCode,
  );
  return parsed?.number ?? '';
}

/** True if the string is nothing but a `+<dial-code>` with no national part. */
export function isOnlyCountryCode(phone: string): boolean {
  if (!phone.startsWith('+')) return false;
  const rest = phone.slice(1);
  return VALID_DIAL_CODES.has(rest as CountryCallingCode);
}

/**
 * Validate an E.164 phone number. Treats empty and "only-country-code" as invalid.
 */
export function isValidE164(phone: string): boolean {
  if (!phone || isOnlyCountryCode(phone)) return false;
  return isValidPhoneNumber(phone);
}
